/**
 * Mise en forme de la Médiathèque : tailles, durées, dates et lecture de la
 * file d'envois. Fonctions pures, sans état ni accès réseau.
 */

const KIB = 1024
const UNITS = ['o', 'Ko', 'Mo', 'Go', 'To'] as const

/**
 * Taille en unités françaises, base 1024 : « 1,7 Go », « 859 Mo », « 3 Mo ».
 * Les octets, Ko et Mo s'affichent sans décimale ; Go et To avec `decimals`.
 */
export function formatBytesFr(bytes: number | null | undefined, decimals = 1): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—'
  let value = bytes
  let unit = 0
  while (value >= KIB && unit < UNITS.length - 1) { value /= KIB; unit++ }
  const digits = unit >= 3 ? decimals : 0
  return `${value.toFixed(digits).replace('.', ',')} ${UNITS[unit]}`
}

/** Durée d'antenne lisible : « 26:00 », « 1:28:30 ». */
export function formatClock(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '—'
  const total = Math.round(sec)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0')
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm.padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Timecode HH:MM:SS:II à la cadence donnée (25 i/s par défaut). */
export function formatTimecode(sec: number | null | undefined, fps = 25): string {
  const safe = sec != null && Number.isFinite(sec) && sec > 0 ? sec : 0
  const rate = Math.max(1, Math.round(fps))
  const frames = Math.floor(safe * rate + 1e-6)
  const ff = frames % rate
  const totalSec = Math.floor(frames / rate)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(totalSec / 3600))}:${pad(Math.floor((totalSec % 3600) / 60))}:${pad(totalSec % 60)}:${pad(ff)}`
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** « 23/09/2026 11:04 » en heure locale du poste. */
export function formatDateTimeFr(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** « 07/10 » : jour et mois d'une prochaine diffusion. */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`
}

const WEEKDAYS = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'] as const

/** « Mer. 07/10 20:35 » : une diffusion dans la liste de la fenêtre d'aperçu. */
export function formatBroadcastSlot(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${WEEKDAYS[d.getDay()]} ${formatDayMonth(iso)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** Cadence et balayage : « 25i », « 25p », « 29,97p ». */
export function formatScan(frameRate: number | null | undefined, fieldOrder: string | null | undefined): string | null {
  if (frameRate == null || !Number.isFinite(frameRate) || frameRate <= 0) return null
  const rounded = Math.round(frameRate * 100) / 100
  const rate = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0$/, '').replace('.', ',')
  const interlaced = fieldOrder != null && fieldOrder !== 'progressive' && fieldOrder !== 'unknown'
  return `${rate}${interlaced ? 'i' : 'p'}`
}

/** Débit vidéo : « 8 Mb/s », « 850 kb/s ». */
export function formatBitrate(bps: number | null | undefined): string | null {
  if (bps == null || !Number.isFinite(bps) || bps <= 0) return null
  if (bps >= 1_000_000) return `${(Math.round(bps / 100_000) / 10).toString().replace('.', ',')} Mb/s`
  return `${Math.round(bps / 1000)} kb/s`
}

/** Audio : « AAC 2.0 · 48 kHz ». */
export function formatAudio(codec: string | null | undefined, layout: string | null | undefined, sampleRate: number | null | undefined): string | null {
  const parts: string[] = []
  const channels = layout === 'stereo' ? '2.0' : layout === 'mono' ? '1.0' : layout ?? null
  const head = [codec ? codec.toUpperCase() : null, channels].filter(Boolean).join(' ')
  if (head) parts.push(head)
  if (sampleRate) parts.push(`${(sampleRate / 1000).toString().replace('.', ',')} kHz`)
  return parts.length ? parts.join(' · ') : null
}

/** Temps restant : « 16 s restantes », « 1 min restante », « 2 min restantes ». */
export function formatRemaining(sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return null
  const s = Math.round(sec)
  if (s < 60) return `${s} s ${s > 1 ? 'restantes' : 'restante'}`
  const min = Math.round(s / 60)
  if (min < 60) return `${min} min ${min > 1 ? 'restantes' : 'restante'}`
  const h = Math.floor(min / 60)
  return `${h} h ${pad2(min % 60)} restantes`
}

/** Durée d'un envoi terminé : « en 3 s », « en 2 min ». */
export function formatElapsed(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null
  const s = Math.max(1, Math.round(ms / 1000))
  if (s < 60) return `en ${s} s`
  const min = Math.round(s / 60)
  return min < 60 ? `en ${min} min` : `en ${Math.floor(min / 60)} h ${pad2(min % 60)}`
}

/** Pastille d'extension : couleur de famille de format. */
export function extensionTone(ext: string | null | undefined): 'mp4' | 'mov' | 'mxf' | 'transport' | 'image' | 'audio' | 'neutral' {
  const e = (ext ?? '').toLowerCase().replace(/^\./, '')
  if (e === 'mp4' || e === 'm4v') return 'mp4'
  if (e === 'mov') return 'mov'
  if (e === 'mxf') return 'mxf'
  if (e === 'ts' || e === 'mts' || e === 'm2ts') return 'transport'
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'svg'].includes(e)) return 'image'
  if (['wav', 'mp3', 'flac', 'aac', 'm4a', 'ogg', 'aif', 'aiff'].includes(e)) return 'audio'
  return 'neutral'
}

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : ''
}

/** Dernier segment d'un chemin relatif : le nom affiché d'un dossier. */
export function folderLabel(relPath: string): string {
  const parts = relPath.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? 'racine'
}

// ─── File d'envois ────────────────────────────────────────────────────────────

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'error' | 'canceled'

export interface QueueTaskLike {
  id:      string
  status:  UploadStatus
  size:    number
  loaded:  number
  speed:   number
  etaSec:  number | null
}

const DISPLAY_ORDER: Record<UploadStatus, number> = { uploading: 0, pending: 1, done: 2, error: 3, canceled: 4 }

/**
 * Ordre d'affichage du suivi : l'envoi en cours, la file dans son ordre, puis
 * les terminés et les échecs. Stable dans chaque groupe.
 */
export function sortForDisplay<T extends QueueTaskLike>(tasks: readonly T[]): T[] {
  return tasks
    .map((task, index) => ({ task, index }))
    .sort((a, b) => DISPLAY_ORDER[a.task.status] - DISPLAY_ORDER[b.task.status] || a.index - b.index)
    .map(entry => entry.task)
}

export interface QueueSummary {
  total:     number
  /** Rang de l'envoi en cours (1-based), ou null si rien ne part. */
  position:  number | null
  active:    number
  failed:    number
  finished:  number
  /** Temps restant pour vider la file, à la vitesse courante. */
  remainingSec: number | null
  /** Progression de l'envoi en cours, 0–100. */
  currentPercent: number | null
}

export function percentOf(task: Pick<QueueTaskLike, 'loaded' | 'size'>): number {
  return task.size > 0 ? Math.min(100, Math.floor((task.loaded / task.size) * 100)) : 0
}

/**
 * Lecture de la file pour l'en-tête du suivi : « 1 sur 4 · 2 min restantes ».
 * Le rang compte les envois déjà traités (réussis, échoués ou annulés) ; le
 * temps restant ajoute aux octets du fichier en cours ceux de la file, à la
 * vitesse mesurée.
 */
export function summarizeQueue(tasks: readonly QueueTaskLike[]): QueueSummary {
  const current = tasks.find(t => t.status === 'uploading') ?? null
  const pending = tasks.filter(t => t.status === 'pending')
  const failed = tasks.filter(t => t.status === 'error').length
  const finished = tasks.filter(t => t.status === 'done' || t.status === 'canceled').length
  const active = pending.length + (current ? 1 : 0)

  let remainingSec: number | null = null
  if (current && current.speed > 0) {
    const bytes = (current.size - current.loaded) + pending.reduce((sum, t) => sum + t.size, 0)
    remainingSec = Math.max(0, Math.round(bytes / current.speed))
  }

  return {
    total: tasks.length,
    position: current || pending.length ? finished + failed + 1 : null,
    active,
    failed,
    finished,
    remainingSec,
    currentPercent: current ? percentOf(current) : null,
  }
}
